import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { curriculumApi, LevelWithProgress, SectionWithProgress } from '../../api/curriculumApi';
import { ArrowLeftIcon, BookOpenIcon, PuzzlePieceIcon, AcademicCapIcon } from '@heroicons/react/24/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * LevelDetailPage Component
 *
 * Displays all sections within a level with exercises.
 * Users can navigate through theory, vocabulary, practice, and flashcards.
 */
const LevelDetailPage: React.FC = () => {
  const { levelId } = useParams<{ levelId: string }>();
  const navigate = useNavigate();

  const [level, setLevel] = useState<LevelWithProgress | null>(null);
  const [sections, setSections] = useState<SectionWithProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (levelId) {
      loadLevelData(parseInt(levelId));
    }
  }, [levelId]);

  const loadLevelData = async (id: number) => {
    try {
      setLoading(true);
      const [levelData, sectionsData] = await Promise.all([
        curriculumApi.getLevel(id),
        curriculumApi.getSectionsForLevel(id),
      ]);
      setLevel(levelData);
      setSections(sectionsData);
    } catch (err) {
      console.error('Failed to load level data:', err);
      setError('Failed to load level. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1); // Go back to world detail page
  };

  const handleSectionClick = (section: SectionWithProgress) => {
    // For now, navigate to first exercise in section
    // TODO: Create SectionDetailPage to show all exercises
    if (section.exercises && section.exercises.length > 0) {
      const firstExercise = section.exercises[0];
      navigate(`/learn/exercise/${firstExercise.id}`);
    }
  };

  const getSectionIcon = (sectionType: string) => {
    switch (sectionType) {
      case 'theory':
        return <BookOpenIcon className="h-6 w-6" />;
      case 'vocabulary':
        return <AcademicCapIcon className="h-6 w-6" />;
      case 'practice':
      case 'flashcard':
        return <PuzzlePieceIcon className="h-6 w-6" />;
      default:
        return <BookOpenIcon className="h-6 w-6" />;
    }
  };

  const getSectionBadgeColor = (sectionType: string) => {
    switch (sectionType) {
      case 'theory':
        return 'badge-info';
      case 'vocabulary':
        return 'badge-secondary';
      case 'practice':
        return 'badge-primary';
      case 'flashcard':
        return 'badge-accent';
      default:
        return 'badge-neutral';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (error || !level) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <div className="alert alert-error max-w-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error || 'Level not found'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100 pb-20">
      {/* Header */}
      <div className="bg-base-200 py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <button
            onClick={handleBackClick}
            className="btn btn-ghost btn-sm mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </button>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-base-content/60">
                Level {level.order}
              </span>
              <div className={`badge ${getSectionBadgeColor('theory')}`}>
                {level.difficulty || 'A1'}
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-base-content">
              {level.title}
            </h1>
            <p className="text-base-content/70 mt-2">
              {level.description}
            </p>
            <div className="flex gap-4 mt-4">
              <div className="badge badge-ghost">
                ⏱️ {level.estimated_minutes} minutes
              </div>
              <div className="badge badge-ghost">
                ⭐ {level.xp_reward} XP
              </div>
              {level.total_sections && (
                <div className="badge badge-ghost">
                  📚 {level.completed_sections || 0}/{level.total_sections} sections completed
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sections List */}
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="space-y-4">
          {sections.map((section, index) => {
            const isCompleted = section.progress?.is_completed || false;
            const exerciseCount = section.exercises?.length || 0;
            const hasExercises = exerciseCount > 0;

            return (
              <div
                key={section.id}
                onClick={() => hasExercises && handleSectionClick(section)}
                className={`
                  card bg-base-200 shadow-md transition-all duration-200
                  ${hasExercises ? 'hover:shadow-lg hover:scale-[1.02] cursor-pointer' : 'opacity-60'}
                  ${isCompleted ? 'border-2 border-success' : ''}
                `}
              >
                <div className="card-body p-6">
                  <div className="flex items-center gap-4">
                    {/* Section Icon */}
                    <div className={`
                      flex items-center justify-center w-14 h-14 rounded-lg shrink-0
                      ${isCompleted ? 'bg-success text-base-100' : 'bg-base-300 text-base-content'}
                    `}>
                      {isCompleted ? (
                        <CheckCircleIcon className="h-7 w-7" />
                      ) : (
                        getSectionIcon(section.section_type)
                      )}
                    </div>

                    {/* Section Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-base-content">
                          {section.title}
                        </h3>
                        <div className={`badge badge-sm ${getSectionBadgeColor(section.section_type)}`}>
                          {section.section_type}
                        </div>
                      </div>

                      {/* Exercise count */}
                      {hasExercises ? (
                        <p className="text-sm text-base-content/60">
                          {exerciseCount} {exerciseCount === 1 ? 'exercise' : 'exercises'}
                        </p>
                      ) : (
                        <p className="text-sm text-base-content/60">
                          Study material • No exercises
                        </p>
                      )}

                      {/* Progress indicator for sections with exercises */}
                      {hasExercises && section.progress && (
                        <div className="mt-2">
                          <progress
                            className="progress progress-primary w-full h-2"
                            value={section.progress.exercises_completed || 0}
                            max={section.progress.total_exercises || 1}
                          ></progress>
                          <p className="text-xs text-base-content/60 mt-1">
                            {section.progress.exercises_completed || 0}/{section.progress.total_exercises || 0} completed
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Arrow icon (only for clickable sections) */}
                    {hasExercises && (
                      <div>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={`
                          w-6 h-6
                          ${isCompleted ? 'text-success' : 'text-primary'}
                        `}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {sections.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-2xl font-bold mb-2">No Sections Available</h3>
            <p className="text-base-content/70">
              This level doesn't have any content yet. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LevelDetailPage;
